import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  Search,
  User,
  Clock,
  Check,
  CheckCheck,
  RefreshCw,
  ArrowLeft,
  Users
} from 'lucide-react';

const MessagesPage = () => {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [admins, setAdmins] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      scrollToBottom();
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, [token]);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data.filter(a => a.id !== user.id));
    } catch (error) {
      // User might not be admin, try to get community contacts instead
      setAdmins([]);
    }
  };

  useEffect(() => {
    if (token) {
      fetchConversations();
      fetchAdmins();
    }
  }, [token, fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      
      // Poll for new messages every 5 seconds
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(selectedConversation.id);
        fetchConversations();
      }, 5000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedConversation, fetchMessages, fetchConversations]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/conversations/${selectedConversation.id}/messages?content=${encodeURIComponent(newMessage)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStartConversation = async (recipientId) => {
    try {
      const response = await axios.post(
        `${API}/conversations?recipient_id=${recipientId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConversation(response.data);
      setShowNewChat(false);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const getOtherParticipant = (conversation) => {
    const otherId = conversation.participant_ids.find(id => id !== user.id);
    return {
      id: otherId,
      name: conversation.participant_names[otherId] || 'Unknown'
    };
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold mb-2">Sign In Required</h2>
          <p className="text-slate-500">Please sign in to access your messages</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden" style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
          <div className="grid md:grid-cols-3 h-full">
            {/* Conversations List */}
            <div className={`border-r ${selectedConversation ? 'hidden md:block' : ''}`}>
              <div className="p-4 border-b">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-bold">Messages</h2>
                  <Button 
                    size="sm" 
                    onClick={() => setShowNewChat(true)}
                    className="bg-plp-blue hover:bg-plp-blue-light"
                    data-testid="new-chat-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    New
                  </Button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              <ScrollArea className="h-[calc(100%-100px)]">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-plp-blue" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">No conversations yet</p>
                    <Button 
                      variant="link" 
                      onClick={() => setShowNewChat(true)}
                      className="text-plp-blue"
                    >
                      Start a new chat
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {conversations
                      .filter(c => {
                        const other = getOtherParticipant(c);
                        return other.name.toLowerCase().includes(searchQuery.toLowerCase());
                      })
                      .map((conversation) => {
                        const other = getOtherParticipant(conversation);
                        const unread = conversation.unread_count?.[user.id] || 0;
                        
                        return (
                          <button
                            key={conversation.id}
                            onClick={() => setSelectedConversation(conversation)}
                            className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                              selectedConversation?.id === conversation.id ? 'bg-plp-blue/5' : ''
                            }`}
                            data-testid={`conversation-${conversation.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="bg-plp-blue/10">
                                <AvatarFallback className="text-plp-blue text-sm">
                                  {getInitials(other.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium truncate">{other.name}</p>
                                  {conversation.last_message_at && (
                                    <span className="text-xs text-slate-400">
                                      {formatTime(conversation.last_message_at)}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between">
                                  <p className="text-sm text-slate-500 truncate">
                                    {conversation.last_message || 'No messages yet'}
                                  </p>
                                  {unread > 0 && (
                                    <Badge className="bg-plp-blue text-white text-xs ml-2">
                                      {unread}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Chat Area */}
            <div className={`md:col-span-2 flex flex-col ${!selectedConversation ? 'hidden md:flex' : ''}`}>
              {showNewChat ? (
                <div className="flex-1 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Button variant="ghost" size="sm" onClick={() => setShowNewChat(false)}>
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <h3 className="font-heading text-lg font-bold">New Conversation</h3>
                  </div>
                  
                  <p className="text-slate-500 mb-4">Select a team member to message:</p>
                  
                  <div className="space-y-2">
                    {admins.length === 0 ? (
                      <p className="text-slate-400 text-sm">No team members available</p>
                    ) : (
                      admins.map((admin) => (
                        <button
                          key={admin.id}
                          onClick={() => handleStartConversation(admin.id)}
                          className="w-full p-4 flex items-center gap-3 rounded-lg border hover:bg-slate-50 transition-colors"
                        >
                          <Avatar className="bg-plp-blue/10">
                            <AvatarFallback className="text-plp-blue">
                              {getInitials(admin.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-left">
                            <p className="font-medium">{admin.name}</p>
                            <p className="text-sm text-slate-500">{admin.email}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="md:hidden"
                      onClick={() => setSelectedConversation(null)}
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <Avatar className="bg-plp-blue/10">
                      <AvatarFallback className="text-plp-blue">
                        {getInitials(getOtherParticipant(selectedConversation).name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{getOtherParticipant(selectedConversation).name}</p>
                      <p className="text-xs text-slate-400">Direct Message</p>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-8">
                          <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500">No messages yet. Start the conversation!</p>
                        </div>
                      ) : (
                        messages.map((message) => {
                          const isOwn = message.sender_id === user.id;
                          return (
                            <div
                              key={message.id}
                              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[70%] ${isOwn ? 'order-1' : ''}`}>
                                <div
                                  className={`p-3 rounded-2xl ${
                                    isOwn 
                                      ? 'bg-plp-blue text-white rounded-br-md' 
                                      : 'bg-slate-100 text-slate-900 rounded-bl-md'
                                  }`}
                                >
                                  <p className="text-sm">{message.content}</p>
                                </div>
                                <div className={`flex items-center gap-1 mt-1 text-xs text-slate-400 ${isOwn ? 'justify-end' : ''}`}>
                                  <span>{formatTime(message.created_at)}</span>
                                  {isOwn && (
                                    message.read 
                                      ? <CheckCheck className="w-3 h-3 text-plp-blue" />
                                      : <Check className="w-3 h-3" />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        disabled={sendingMessage}
                        data-testid="message-input"
                      />
                      <Button 
                        type="submit" 
                        disabled={sendingMessage || !newMessage.trim()}
                        className="bg-plp-blue hover:bg-plp-blue-light"
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-bold text-slate-700 mb-2">Your Messages</h3>
                    <p className="text-slate-500 mb-4">Select a conversation or start a new chat</p>
                    <Button onClick={() => setShowNewChat(true)} className="bg-plp-blue">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      New Conversation
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
